import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { SITE_ORIGIN, SITE_NAME, DEFAULT_OG_IMAGE } from '../../data/businessInfo';

export interface SeoProps {
	title: string;
	description?: string;
	/**
	 * Query-param-free path, e.g. '/blog/akkuproblemak'. Defaults to the
	 * current pathname so query strings (?utm_* etc.) never leak into the
	 * canonical. Pass an explicit path for routes that should collapse
	 * variants to one canonical.
	 */
	canonicalPath?: string;
	image?: string;
	type?: 'website' | 'article';
	noindex?: boolean;
	/** Override og:title / twitter:title when the CMS supplies distinct OG copy. */
	ogTitle?: string;
	/** Override og:description / twitter:description. */
	ogDescription?: string;
}

function toAbsolute(url?: string): string {
	if (!url) return DEFAULT_OG_IMAGE;
	return /^https?:\/\//i.test(url) ? url : `${SITE_ORIGIN}${url.startsWith('/') ? '' : '/'}${url}`;
}

/**
 * Centralises every per-route <head> tag: title, description, an absolute
 * non-www param-free canonical, Open Graph, Twitter Card, and robots noindex.
 * Pages should use this instead of hand-writing <Helmet> head tags.
 * (JSON-LD scripts stay in <JsonLd>.)
 *
 * Same component as akkubolt's src/components/Seo/Seo.tsx — keep in sync.
 */
export default function Seo({
	title,
	description,
	canonicalPath,
	image,
	type = 'website',
	noindex = false,
	ogTitle,
	ogDescription,
}: SeoProps) {
	const location = useLocation();
	// Canonical is always the param-free path on the non-www host.
	const rawPath = (canonicalPath ?? location.pathname).split('?')[0].split('#')[0];
	const path = rawPath === '/' ? '/' : rawPath.replace(/\/$/, '');
	const canonical = `${SITE_ORIGIN}${path}`;
	const ogImage = toAbsolute(image || DEFAULT_OG_IMAGE);
	const socialTitle = ogTitle || title;
	const socialDescription = ogDescription || description;

	return (
		<Helmet>
			<title>{title}</title>
			{description && <meta name="description" content={description} />}
			<link rel="canonical" href={canonical} />
			{noindex && <meta name="robots" content="noindex, follow" />}

			<meta property="og:type" content={type} />
			<meta property="og:site_name" content={SITE_NAME} />
			<meta property="og:locale" content="hu_HU" />
			<meta property="og:title" content={socialTitle} />
			{socialDescription && <meta property="og:description" content={socialDescription} />}
			<meta property="og:url" content={canonical} />
			<meta property="og:image" content={ogImage} />

			<meta name="twitter:card" content="summary_large_image" />
			<meta name="twitter:title" content={socialTitle} />
			{socialDescription && <meta name="twitter:description" content={socialDescription} />}
			<meta name="twitter:image" content={ogImage} />
		</Helmet>
	);
}
